"use client";

import { CurvedRowList } from "@/components/feature/curved-row-list";
import { GameCard } from "@/components/feature/game-card";
import { RiveTile } from "@/components/feature/rive-tile";
import type { CurvedRowDirection } from "@/lib/curved-row-layout";
import type { PageOriginContext } from "@/lib/page-origin-context";
import { GameSize } from "@/types/game";
import type { ShowcaseGridDisplayItem } from "@/types/showcase-grid";

interface CurvedRowProps {
  items: ShowcaseGridDisplayItem[];
  direction?: CurvedRowDirection;
  enlargeMiddle?: boolean;
  sidePadding?: number;
  pageOrigin?: PageOriginContext | null;
}

function CurvedRowTile({
  item,
  pageOrigin,
}: {
  item: ShowcaseGridDisplayItem;
  pageOrigin?: PageOriginContext | null;
}) {
  if (item.type === "game") {
    // No overflow clip here — GameCard already rounds the art, and the NEW badge
    // sits outside the corner.
    return (
      <GameCard
        game={{ ...item.game, size: GameSize.Small }}
        size="small"
        pageOrigin={pageOrigin}
        linkPrefetch={false}
        previewIdOverride={item.id}
      />
    );
  }
  if (item.type === "rive") {
    return <RiveTile src={item.assetUrl} className="h-full w-full" ariaLabel="" />;
  }
  return (
    // User-supplied section art.
    // eslint-disable-next-line @next/next/no-img-element
    <img src={item.assetUrl} alt="" className="h-full w-full object-contain" />
  );
}

/**
 * Public curved row: PLA-834 packing (mobile 6-col rearrange, desktop arc/crown).
 * Extra tiles beyond 6 are ignored. Tile size comes from the track, not game.size.
 */
export function CurvedRow({
  items,
  direction = "up",
  enlargeMiddle = true,
  sidePadding = 0,
  pageOrigin,
}: CurvedRowProps) {
  return (
    <CurvedRowList
      items={items}
      direction={direction}
      enlargeMiddle={enlargeMiddle}
      sidePadding={sidePadding}
      getKey={(item) => item.id}
      renderItem={(item) => <CurvedRowTile item={item} pageOrigin={pageOrigin} />}
    />
  );
}
